import { isAdminApiUrl } from './stepUp';
import { API_BASE_URL } from 'src/utils/api-url';

/**
 * XBIT-4777 D16: the handlers the service guards with StepUpGuard, one entry each. The console sends
 * the step-up header only on these, so an edge that stops allowing the header breaks only custody requests.
 * XBIT-5072: prod guards only the Vault routes. The finance, relay and xpx-perps entries of XBIT-4777 stay on unstable.
 * Keep it equal to `test/step-up-coverage.spec.ts` in xbit-admin-service: a ticket that guards another route
 * edits both lists.
 */
type Method = 'GET' | 'POST' | 'PUT';

const route = (method: Method, pattern: string): { method: Method; path: RegExp } => ({
  method,
  path: new RegExp(`^${pattern.replace(/:[A-Za-z_]+/g, '[^/]+')}/?$`),
});

export const STEP_UP_ROUTES: ReadonlyArray<{ method: Method; path: RegExp }> = [
  // XBIT-4863: reconciliation (a share adjustment mints or burns shares; a run can freeze a vault).
  route('POST', '/vault/vaults/:id/reconciliation/adjustments/:proposalId/approve'),
  route('POST', '/vault/vaults/:id/reconciliation/resolutions'),
  route('POST', '/vault/vaults/:id/reconciliation/runs'),
  // XBIT-4869: the Operations queue's approvals and the release of a quarantine hold.
  route('POST', '/vault/operations/work-orders/:id/resolutions/:proposalId/approve'),
  route('POST', '/vault/operations/closures/:id/resolutions/:proposalId/approve'),
  route('POST', '/vault/operations/vaults/:id/refund-proposals/:proposalId/approve'),
  route('POST', '/vault/operations/vaults/:id/quarantined-transfers/:transferId/release-hold'),
  // XBIT-4993: approving a treasury lane resume re-opens the payout lanes, which is the direction
  // that can lose money. Pausing and proposing are not step-up: stopping money must never be slowed.
  route('POST', '/vault/approvals/lane-pauses/resume/approve'),
];

/** True only for an absolute admin API URL whose method and path (query and fragment dropped) match an entry. */
export function isStepUpRoute(method: string | undefined, url: string | undefined): boolean {
  if (!isAdminApiUrl(url)) return false;
  const path = url.slice(API_BASE_URL.length).split('#')[0].split('?')[0];
  const verb = (method || 'GET').toUpperCase();
  return STEP_UP_ROUTES.some((entry) => entry.method === verb && entry.path.test(path));
}
